import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import type { TRPCRouter } from '@tempo-io/notification-app/trpc';
import {
  createTRPCClient,
  httpBatchLink,
  httpSubscriptionLink,
  retryLink,
  splitLink,
} from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import { useState } from 'react';
import SuperJSON from 'superjson';
import { SingleAttemptEventSource } from '@/common/trpc/singleAttemptEventSource';
import { createSseRetryPolicy } from '@/common/trpc/sseRetryPolicy';
import { createSseTokenProvider } from '@/common/trpc/sseTokenProvider';
import { getEnv } from '@/common/utils/getEnv';

const trpcContext = createTRPCContext<TRPCRouter>();

function useTRPCClient() {
  const { getAccessTokenSilently } = useAuth0();
  const env = getEnv();
  const url = `${env.apiBaseUrl}/api/sse/v1/trpc`;
  const [client] = useState(() => {
    const retryPolicy = createSseRetryPolicy();
    const getSubscriptionToken = createSseTokenProvider(getAccessTokenSilently);

    return createTRPCClient<TRPCRouter>({
      links: [
        splitLink({
          condition: (op) => op.type === 'subscription',
          true: [
            retryLink({ retry: retryPolicy.retry, retryDelayMs: retryPolicy.retryDelayMs }),
            httpSubscriptionLink({
              url,
              transformer: SuperJSON,
              EventSource: SingleAttemptEventSource,
              eventSourceOptions: async ({ op }) => {
                const token = await getSubscriptionToken({
                  forceRefresh: retryPolicy.consumeFreshTokenRequest(op.id),
                });
                return {
                  headers: {
                    authorization: `Bearer ${token}`,
                  },
                  heartbeatTimeout: 120_000,
                };
              },
            }),
          ],
          false: httpBatchLink({
            url,
            transformer: SuperJSON,
            async headers() {
              const token = await getAccessTokenSilently();
              return {
                authorization: `Bearer ${token}`,
              };
            },
          }),
        }),
      ],
    });
  });

  return client;
}

export const TRPCProvider = ({ children }: { children: React.ReactNode }) => {
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();
  return (
    <trpcContext.TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
      {children}
    </trpcContext.TRPCProvider>
  );
};

export const { useTRPC } = trpcContext;
